/**
 * Snapshot assembly: revision stamping, size bounding, and the payload a
 * consumer transports.
 *
 * The core deliberately names no wire format. It emits
 * `{ entityId, data: { snapshot, capabilities } }`; mapping that onto a chat
 * context, a telemetry event or anything else belongs to the consumer.
 */
import { noteSnapshotError, recordPayload } from './diagnostics';
import { activeEntry, type RegistryEntry } from './registry';
import type {
  ViewSurfaceDerived,
  ViewSurfaceField,
  ViewSurfaceManifest,
  ViewSurfaceRegistration,
  ViewSurfaceScreenData,
  ViewSurfaceSection,
  ViewSurfaceSnapshot,
  ViewSurfaceSnapshotDraft,
} from './types';

/**
 * Budget for the whole payload, in `JSON.stringify` length (UTF-16 code
 * units, not bytes) — close enough for a prompt budget and far cheaper than
 * encoding. A realistic PrimeX underwriting quote measured ~8.5k.
 */
export const MAX_SCREEN_DATA_CHARS = 12_000;

function sizeOf(value: unknown): number {
  return JSON.stringify(value).length;
}

/**
 * The manifest is DERIVED from the registered capabilities rather than being
 * a hand-maintained record of flags. A capability nobody registered is simply
 * absent, so there is no second list to fall out of sync.
 */
export function manifestOf(
  registration: ViewSurfaceRegistration,
  entityType: string
): ViewSurfaceManifest {
  return {
    version: 1,
    viewId: registration.viewId,
    entityType,
    capabilities: (registration.capabilities ?? []).map((c) => c.describe()),
  };
}

/**
 * Fit the payload to the budget, working from the lowest-priority end.
 *
 * At each step the LAST section either sheds an item or, once it has nothing
 * left to shed, is removed entirely. Shedding before removing is the point: a
 * partial list still answers most questions, whereas a missing section
 * answers none. An earlier version dropped whole sections first and only
 * trimmed the survivor, which deleted the largest collection outright
 * whenever a field group happened to be highest priority.
 *
 * The first section is never removed. Everything removed is named in
 * `truncated`, so a consumer can say "I can't see the rest of X" rather than
 * quietly reasoning over a partial view.
 */
export function boundSections(
  snapshot: ViewSurfaceSnapshot,
  budget: number = MAX_SCREEN_DATA_CHARS
): {
  sections: readonly ViewSurfaceSection[];
  truncated: readonly string[];
} {
  let kept: ViewSurfaceSection[] = [...snapshot.sections];
  const droppedIds: string[] = [];
  const trimmedIds: string[] = [];

  const truncatedOf = (): string[] => [
    ...trimmedIds.map((id) => `${id}.items`),
    ...droppedIds,
  ];
  // Measure the payload as it will actually be emitted — `truncated` grows as
  // we shed, and leaving it out of the measurement lets the result overshoot.
  const overBudget = (): boolean =>
    sizeOf({ ...snapshot, sections: kept, truncated: truncatedOf() }) > budget;

  while (overBudget()) {
    const index = kept.length - 1;
    const last = kept[index];
    if (!last) {
      break;
    }

    if (last.kind === 'collection' && last.items.length > 0) {
      if (!trimmedIds.includes(last.id)) {
        trimmedIds.push(last.id);
      }
      kept = [
        ...kept.slice(0, index),
        { ...last, items: last.items.slice(0, -1) },
      ];
      continue;
    }

    if (kept.length === 1) {
      break;
    }

    kept = kept.slice(0, index);
    // A section removed outright supersedes any note about its items.
    const trimmedAt = trimmedIds.indexOf(last.id);
    if (trimmedAt >= 0) {
      trimmedIds.splice(trimmedAt, 1);
    }
    droppedIds.unshift(last.id);
  }

  return { sections: kept, truncated: truncatedOf() };
}

/**
 * Stamp `revision` and `derivedAtRevision`.
 *
 * `busy` is excluded from the input hash: it flips on and off around every
 * refresh, and treating that as an input change would invalidate every
 * in-flight proposal for no reason.
 *
 * Settling is read from `status`, not from a diff of `values`. Diffing was
 * wrong — a refresh that completes and returns identical values produces no
 * change, so the revision would stay behind forever and a consumer would
 * believe the derived block was perpetually stale. `invalid` and
 * `unavailable` deliberately do not settle: in those states the values
 * genuinely do not reflect the inputs.
 */
function stampRevisions(
  entry: RegistryEntry,
  draft: ViewSurfaceSnapshotDraft
): {
  revision: number;
  committedRevision: number;
  derived?: readonly ViewSurfaceDerived[];
} {
  const inputHash = JSON.stringify({
    summary: draft.summary,
    sections: draft.sections,
    readOnly: draft.readOnly,
  });
  if (inputHash !== entry.lastInputHash) {
    entry.lastInputHash = inputHash;
    entry.revision += 1;
  }

  // A surface that describes its committed state gets a second counter that
  // moves only when THAT moves. One that does not keeps the two identical,
  // which is what every view did before the overlay existed.
  const baseFingerprint = entry.registration.getBaseFingerprint?.();
  if (baseFingerprint === undefined) {
    entry.committedRevision = entry.revision;
  } else if (baseFingerprint !== entry.lastBaseHash) {
    entry.lastBaseHash = baseFingerprint;
    entry.committedRevision += 1;
  }

  if ((draft.derived ?? []).every((d) => d.status === 'current')) {
    entry.derivedRevision = entry.revision;
  }

  if (!draft.derived) {
    return {
      revision: entry.revision,
      committedRevision: entry.committedRevision,
    };
  }

  return {
    revision: entry.revision,
    committedRevision: entry.committedRevision,
    derived: draft.derived.map((d) => ({
      ...d,
      derivedAtRevision: entry.derivedRevision,
    })),
  };
}

function strippedFields(
  fields: readonly ViewSurfaceField[]
): readonly ViewSurfaceField[] {
  return fields.map(({ options: _o, optionsTruncated: _t, ...rest }) => rest);
}

/**
 * Drop every field's enumerated options.
 *
 * Applied before bounding, not after: options are the largest thing in a
 * field, so measuring the payload with them still in would shed items to fit a
 * size nothing is going to send.
 */
function withoutFieldOptions(
  sections: readonly ViewSurfaceSection[]
): readonly ViewSurfaceSection[] {
  return sections.map((section) => {
    if (section.kind === 'fields') {
      return { ...section, fields: strippedFields(section.fields) };
    }
    return {
      ...section,
      items: section.items.map((item) => ({
        ...item,
        fields: strippedFields(item.fields),
      })),
    };
  });
}

/**
 * Build the current snapshot for a given entry, stamped and bounded.
 *
 * `forTransport` applies the registration's transport-only reductions. An
 * action dispatch omits it deliberately: it validates against the full
 * snapshot, which is what keeps a consumer's omissions from widening what the
 * view will accept.
 */
export function buildSnapshot(
  entry: RegistryEntry,
  forTransport = false
): ViewSurfaceSnapshot {
  const draft = entry.registration.getSnapshot();
  const stamped = stampRevisions(entry, draft);

  // Drop the draft's `derived` before spreading: it is the unstamped shape,
  // and a plain spread would leave it in place whenever the stamped result
  // omits the key (a surface with no derived tier).
  const { derived: _unstamped, ...rest } = draft;
  const reduced =
    forTransport && entry.registration.omitFieldOptions
      ? withoutFieldOptions(draft.sections)
      : draft.sections;
  const full: ViewSurfaceSnapshot = {
    ...rest,
    sections: reduced,
    // Stamped here rather than supplied by the builder: the key belongs to the
    // registration, and a builder that invented its own could disagree with
    // the registry it is filed under.
    viewKey: entry.registration.viewKey,
    revision: stamped.revision,
    // Its own counter now, and equal to `revision` for every surface that does
    // not supply `getBaseFingerprint` — see the type and `stampRevisions`.
    committedRevision: stamped.committedRevision,
    ...(stamped.derived ? { derived: stamped.derived } : {}),
    truncated: [],
  };

  const { sections, truncated } = boundSections(
    full,
    entry.registration.maxScreenDataChars ?? MAX_SCREEN_DATA_CHARS
  );
  return { ...full, sections, truncated };
}

/**
 * Read the active surface's snapshot WITHOUT recording it as a payload.
 *
 * Control flow — waiting for a refresh to settle, say — reads the snapshot
 * repeatedly, and counting those as sends would wreck the delivery metrics
 * that answer "is this surface reaching anything?". Revision stamping is
 * idempotent for unchanged content, so peeking never bumps a revision on its
 * own.
 */
export function peekViewSurfaceSnapshot(): ViewSurfaceSnapshot | null {
  const entry = activeEntry();
  if (!entry) {
    return null;
  }
  try {
    return buildSnapshot(entry);
  } catch (err) {
    noteSnapshotError(err);
    return null;
  }
}

/**
 * Capture-at-read payload for the active surface.
 *
 * Never throws: a broken snapshot builder must not break whatever the
 * consumer was doing — sending a message, emitting telemetry, anything.
 */
export function getViewSurfaceScreenData(): ViewSurfaceScreenData | null {
  const entry = activeEntry();
  if (!entry) {
    return null;
  }

  try {
    const snapshot = buildSnapshot(entry, true);
    const capabilities = manifestOf(entry.registration, snapshot.entityType);
    recordPayload(snapshot, capabilities);
    return {
      entityId: entry.registration.entityId,
      data: { snapshot, capabilities },
    };
  } catch (err) {
    noteSnapshotError(err);
    return null;
  }
}
